// Слой ядра для UI (§5 задания Р-09): понятные операции и наблюдаемые данные.
//
// UI не ходит в сеть и не знает про Connect: он читает локальную базу и подписывается на изменения.
// Поэтому здесь нет ни одного метода, который «сходит на сервер и вернёт список», — список всегда
// локальный, а сеть только пополняет базу.

import { Code, ConnectError, createClient, type Transport } from "@connectrpc/connect";
import { ChatService } from "@yofis/proto/yofis/v1/chat_pb";
import { MessageService } from "@yofis/proto/yofis/v1/message_pb";
import { UserService } from "@yofis/proto/yofis/v1/user_pb";
import { NodeService } from "@yofis/proto/yofis/v1/node_pb";
import { LinkService } from "@yofis/proto/yofis/v1/link_pb";
import type { LobbyEntry } from "@yofis/proto/yofis/v1/link_pb";
import type { Chat } from "@yofis/proto/yofis/v1/chat_pb";
import type { Room } from "@yofis/proto/yofis/v1/call_pb";
import type { Device, User } from "@yofis/proto/yofis/v1/common_pb";

import { Outbox } from "./outbox.ts";
import { toLocalMessage } from "./sync.ts";
import { SyncEngine } from "./sync.ts";
import type { LocalChat, LocalMessage, LocalStore } from "./store.ts";

/** Подписка на изменения; вызов возвращённой функции отписывает. */
export type Unsubscribe = () => void;

export interface ClientOptions {
  transport: Transport;
  store: LocalStore;
  /** Адрес текущего пользователя: им подписываются черновики до ответа сервера. */
  me: string;
}

/** Ядро для UI. */
/** Заявка в лобби так, как её видит тот, кто впускает. */
/** Присутствие человека: состояние (`yofis.v1.PresenceState`) и когда его видели в последний раз. */
export interface PresenceUpdate {
  address: string;
  state: number;
  lastSeenMillis: number;
}

/** Кто печатает и до какого момента это считать правдой (сервер гасит через 6 с). */
export interface TypingUpdate {
  chatId: string;
  address: string;
  untilMillis: number;
}

export interface LobbyUpdate {
  targetId: string;
  address: string;
  displayName: string;
  /** `yofis.v1.LobbyState`: 1 — ждёт, 2 — впущен, 3 — отклонён. */
  state: number;
}

/** Вход, о котором стоит сказать человеку (0.16). */
export interface SignInEvent {
  deviceId: string;
  deviceName: string;
  /** `yofis.v1.SignInMethod`: 1 — пароль, 2 — ссылка, 3 — внешний провайдер. */
  method: number;
  atMillis: number;
}

export class YofisClient {
  readonly #transport: Transport;
  readonly #store: LocalStore;
  readonly #sync: SyncEngine;
  readonly #outbox: Outbox;
  readonly #listeners = new Set<() => void>();
  readonly #lobbyWatchers = new Set<(update: LobbyUpdate) => void>();
  readonly #signInWatchers = new Set<(event: SignInEvent) => void>();
  readonly #typingWatchers = new Set<(update: TypingUpdate) => void>();
  readonly #presenceWatchers = new Set<(update: PresenceUpdate) => void>();
  #me: string;

  constructor(options: ClientOptions) {
    this.#transport = options.transport;
    this.#store = options.store;
    this.#me = options.me;
    const changed = (): void => this.#emit();
    this.#sync = new SyncEngine({
      transport: options.transport,
      store: options.store,
      me: () => this.#me,
      listener: {
        changed,
        // Заявки лобби базу не меняют, поэтому идут отдельным каналом — прямо тому, кто впускает.
        lobby: (targetId, address, displayName, state) => {
          for (const watcher of this.#lobbyWatchers) {
            watcher({ targetId, address, displayName, state });
          }
        },
        presence: (address, state, lastSeenMillis) => {
          for (const watcher of this.#presenceWatchers) {
            watcher({ address, state, lastSeenMillis });
          }
        },
        typing: (chatId, address, untilMillis) => {
          for (const watcher of this.#typingWatchers) {
            watcher({ chatId, address, untilMillis });
          }
        },
        signIn: (deviceId, deviceName, method, atMillis) => {
          for (const watcher of this.#signInWatchers) {
            watcher({ deviceId, deviceName, method, atMillis });
          }
        },
      },
    });
    this.#outbox = new Outbox({ transport: options.transport, store: options.store, onChange: changed });
  }

  /**
   * Адрес текущего пользователя: им подписываются черновики до ответа сервера. Задаётся отдельно,
   * потому что при восстановлении сессии адрес известен только после ответа узла, а клиент нужен
   * раньше — чтобы этот ответ и спросить. Пустой адрес означал бы, что свои сообщения показываются
   * как чужие: без подписи «отправляется» и не на своей стороне ленты.
   */
  get me(): string {
    return this.#me;
  }

  set me(address: string) {
    this.#me = address;
  }

  /** Начинает слушать события узла. */
  start(): void {
    this.#sync.start();
  }

  stop(): void {
    this.#sync.stop();
  }

  /** Подписка на любые изменения локальных данных: UI перечитывает то, что показывает. */
  /** Подписка на заявки лобби: их показывает тот, кто впускает. */
  watchLobby(watcher: (update: LobbyUpdate) => void): Unsubscribe {
    this.#lobbyWatchers.add(watcher);
    return () => this.#lobbyWatchers.delete(watcher);
  }

  /** Подписка на присутствие: состояние живое, в базу не попадает. */
  watchPresence(watcher: (update: PresenceUpdate) => void): Unsubscribe {
    this.#presenceWatchers.add(watcher);
    return () => this.#presenceWatchers.delete(watcher);
  }

  /** Подписка на «печатает»: состояние живёт секунды, в базу не попадает и гаснет по сроку. */
  watchTyping(watcher: (update: TypingUpdate) => void): Unsubscribe {
    this.#typingWatchers.add(watcher);
    return () => this.#typingWatchers.delete(watcher);
  }

  /** Подписка на входы, о которых стоит знать: обязана вести к отзыву устройства. */
  watchSignIns(watcher: (event: SignInEvent) => void): Unsubscribe {
    this.#signInWatchers.add(watcher);
    return () => this.#signInWatchers.delete(watcher);
  }

  subscribe(listener: () => void): Unsubscribe {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** Список чатов из локальной базы. */
  chats(options: { includeArchived?: boolean } = {}): LocalChat[] {
    return this.#store.chats(options);
  }

  /** Догружает историю чата с узла, если локально её ещё нет: вызывается при открытии чата. */
  async openChat(chatId: string): Promise<void> {
    // Состав нужен сразу: по нему считается «прочитали все», и без него вторая галочка в группе не
    // появится никогда. Отказ не мешает открыть чат — история важнее состава.
    void this.loadMembers(chatId).catch(() => undefined);
    await this.#sync.history(chatId);
    this.#emit();
  }

  /** История чата страницами, от новых к старым. */
  messages(chatId: string, options: { before?: number; limit?: number } = {}): LocalMessage[] {
    return this.#store.messages(chatId, options);
  }

  /** Отправка: сообщение появляется в базе сразу, сеть догоняет. */
  async send(chatId: string, text: string, replyToId?: string): Promise<void> {
    // Чат обязан быть в базе: на сообщении висит внешний ключ, и без этой проверки отправка в
    // неизвестный чат отвечает «FOREIGN KEY constraint failed» — сообщением, по которому не понять
    // ничего. Само по себе это не случается: чат приходит стримом раньше, чем его можно открыть.
    if (this.#store.chat(chatId) === undefined) {
      throw new Error(`чат ${chatId} не известен клиенту: отправлять некуда`);
    }
    await this.#outbox.send(replyToId === undefined ? { chatId, text } : { chatId, text, replyToId }, this.#me);
  }

  /** Повторная отправка всего, чему пришёл срок: вызывается, когда сеть вернулась. */
  async retryPending(): Promise<void> {
    await this.#outbox.flush();
  }

  /** Сколько сообщений ждёт отправки. */
  get pending(): number {
    return this.#outbox.pending;
  }

  /**
   * Подгрузка старых сообщений: сперва из локальной базы, и только если там кончилось — с узла.
   * Возвращает то, что добавилось, чтобы UI приклеил это к началу ленты, не перечитывая её целиком.
   */
  async older(chatId: string, beforeId: string, limit = 50): Promise<LocalMessage[]> {
    const local = this.#store.messages(chatId, { beforeId, limit });
    if (local.length > 0) {
      return local.reverse();
    }
    await this.loadOlder(chatId, beforeId, limit);
    return this.#store.messages(chatId, { beforeId, limit }).reverse();
  }

  /** Подгрузка старых сообщений: UI просит историю дальше, ядро добирает её с сервера в базу. */
  async loadOlder(chatId: string, beforeId: string, limit = 50): Promise<void> {
    const messages = createClient(MessageService, this.#transport);
    const page = await messages.getMessages({
      chatId,
      limit,
      anchor: { $typeName: "yofis.v1.MessageAnchor", anchor: { case: "beforeId", value: beforeId } },
    });
    for (const m of page.messages) {
      this.#store.upsertMessage(toLocalMessage(m));
    }
    this.#emit();
  }

  /** Отметка прочтения: и на сервере, и в локальном счётчике непрочитанного. */
  async markRead(chatId: string, messageId: string): Promise<void> {
    // Значок гаснет сразу: человек прочитал, и ждать ответа сети незачем. Ошибка отправки его не
    // вернёт — при следующей синхронизации придёт число сервера, и оно всё поправит.
    this.#store.clearUnread(chatId);
    await createClient(ChatService, this.#transport).markRead({ chatId, messageId });
    const chat = this.#store.chat(chatId);
    if (chat !== undefined) {
      this.#store.upsertChat({ ...chat, unread: 0 });
      this.#emit();
    }
  }

  /** «Печатает»: живое событие, в базу не пишется. */
  async setTyping(chatId: string, typing: boolean): Promise<void> {
    await createClient(ChatService, this.#transport).setTyping({ chatId, typing });
  }

  /**
   * Состав чата: спрашивается у узла и кладётся в базу.
   *
   * Без него клиент не знает, кто в чате, — а значит не может сказать «прочитали все» (в группе это
   * и есть вторая галочка) и не может показать список участников. Вызывается при открытии чата:
   * состав меняется редко, а события о его изменении приходят отдельно.
   */
  async loadMembers(chatId: string): Promise<PresenceUpdate[]> {
    const answer = await createClient(ChatService, this.#transport).listMembers({ chatId });
    this.#store.upsertMembers(
      chatId,
      answer.members.map((member) => ({
        address: member.address,
        role: member.role,
        lastReadMessageId: member.lastReadMessageId,
      })),
    );
    // Присутствие приходит тем же ответом и в базу не кладётся: оно живое. Для того, кто только что
    // открыл чат, это единственный способ узнать, кто здесь, — событий он не застал.
    return answer.members.map((member) => ({
      address: member.address,
      state: member.user?.presence?.state ?? 0,
      lastSeenMillis: member.user?.presence?.lastSeen === undefined
        ? 0
        : Number(member.user.presence.lastSeen.seconds) * 1000,
    }));
  }

  /**
   * До какого своего сообщения дочитали остальные участники чата.
   *
   * Пусто — не знаем: отметок ещё не было. Это не то же самое, что «не прочитали», и показывать
   * одно вместо другого нельзя: «прочитано» — утверждение, а у нас его нет.
   */
  readUpTo(chatId: string): string {
    return this.#store.readUpTo(chatId, this.#me);
  }

  /** Реакция: ставится и снимается одной операцией. */
  async setReaction(chatId: string, messageId: string, emoji: string, set: boolean): Promise<void> {
    await createClient(MessageService, this.#transport).setReaction({ chatId, messageId, emoji, set });
  }

  /**
   * Поиск. Локальная история ищется всегда; серверный индекс — только если узел объявил, что он есть
   * (`Capabilities.search_server`). Выдача не подменяется молча: если серверного поиска нет, вернётся
   * то, что нашлось локально, и вызывающий это увидит по флагу.
   */
  async search(query: string, limit = 50): Promise<{ local: LocalMessage[]; server: LocalMessage[]; serverSearched: boolean }> {
    const local = this.#store.search(query, limit);
    const node = await createClient(NodeService, this.#transport).getNodeInfo({});
    if (node.node?.capabilities?.searchServer !== true) {
      return { local, server: [], serverSearched: false };
    }
    try {
      const hits = await createClient(MessageService, this.#transport).searchMessages({ query, limit });
      const server = hits.hits.flatMap((h) => (h.message === undefined ? [] : [toLocalMessage(h.message)]));
      return { local, server, serverSearched: true };
    } catch (err) {
      // Узел объявил возможность и отказал в ней. Так бывает: признак берётся из права пользоваться,
      // а не из наличия работающего метода (найдено на живом стенде 2026-09-23). Чинить это нужно на
      // узле, но терять из-за противоречия УЖЕ НАЙДЕННОЕ локально нельзя: человек искал по своей
      // переписке, и она у него есть. Флаг `serverSearched` остаётся false — молчаливой подмены нет.
      if (isCapabilityDisabled(err)) {
        return { local, server: [], serverSearched: false };
      }
      throw err;
    }
  }

  /** Справочник сотрудников: поиск по имени, логину, должности и отделу; пустой запрос — весь список. */
  async people(query: string, limit = 200): Promise<User[]> {
    const res = await createClient(UserService, this.#transport).searchUsers({ query, limit });
    return res.users;
  }

  /**
   * Профили по адресам — до 100 за раз.
   *
   * Справочник клиент берёт один раз при входе, а люди появляются и после: сотрудник, которого
   * добавили в чат сегодня, приходит в событиях адресом. Искать его поиском нельзя — поиск смотрит
   * на имя и логин, а не на адрес, и по «nina@company.ru» не находит никого.
   */
  async users(addresses: string[]): Promise<User[]> {
    if (addresses.length === 0) {
      return [];
    }
    const res = await createClient(UserService, this.#transport).getUsers({ addresses: addresses.slice(0, 100) });
    return res.users;
  }

  /**
   * Вход по ссылке в чат или встречу.
   *
   * Возвращает три разных исхода, и они не взаимозаменяемы: чат — вошёл в переписку, комната — вошёл
   * во встречу, ожидание лобби — не вошёл никуда и ждёт решения. Подменять третье первым нельзя:
   * человек решит, что уже внутри.
   */
  async joinByLink(token: string): Promise<{ chat: Chat | undefined; room: Room | undefined; lobbyPending: boolean }> {
    const res = await createClient(LinkService, this.#transport).joinByLink({ token });
    return { chat: res.chat, room: res.room, lobbyPending: res.lobbyPending };
  }

  /** Заявки в лобби чата или встречи — для тех, кто впускает. */
  async lobby(targetId: string): Promise<LobbyEntry[]> {
    const res = await createClient(LinkService, this.#transport).listLobby({ targetId });
    return res.entries;
  }

  /** Впустить просящегося. */
  async admitToLobby(targetId: string, address: string): Promise<void> {
    await createClient(LinkService, this.#transport).admitLobby({ targetId, address });
  }

  /** Отклонить заявку. */
  async rejectFromLobby(targetId: string, address: string): Promise<void> {
    await createClient(LinkService, this.#transport).rejectLobby({ targetId, address });
  }

  /** Профиль текущего пользователя с узла. */
  async profile(): Promise<User | undefined> {
    const res = await createClient(UserService, this.#transport).getMe({});
    return res.user;
  }

  /** Устройства пользователя: список и отзыв — то, что показывает раздел безопасности. */
  async devices(): Promise<Device[]> {
    const res = await createClient(UserService, this.#transport).listMyDevices({});
    return res.devices;
  }

  async revokeDevice(deviceId: string): Promise<void> {
    await createClient(UserService, this.#transport).revokeDevice({ deviceId });
  }

  /** Полная перезагрузка с сервера (после `resync_required` или по кнопке «обновить»). */
  async reload(): Promise<void> {
    await this.#sync.reload();
    this.#emit();
  }

  #emit(): void {
    for (const listener of this.#listeners) {
      listener();
    }
  }
}

/**
 * Отказ «возможность выключена» — единственный, который поиск переживает без потери локальной выдачи.
 *
 * На проводе он приезжает кодом `unimplemented` (карта `yerr` на сервере): «метода тут нет». Ошибки
 * сети, сессии и прав сюда не попадают и по-прежнему выходят наружу — их прятать нельзя.
 */
function isCapabilityDisabled(err: unknown): boolean {
  return err instanceof ConnectError && err.code === Code.Unimplemented;
}
